/**
 * SettingsMenu — overlay panel for audio, display, and controls settings.
 *
 * Accessible from both the pause screen and (future) lobby menu.
 * All text from locale. All colors from palette. Fixed to camera.
 * Settings persist to localStorage via the provided callbacks.
 *
 * Two renderings, chosen once in wireAudio():
 *   - C5 flag ON  → a DOM console sheet: ink-line sliders with a brass bead,
 *     wax-seal toggles, specimen-label section headers (see c5/C5Panel).
 *   - C5 flag OFF → the original Phaser panel, untouched.
 * Every persisted setting and callback is identical between the two.
 */

import * as Phaser from 'phaser';
import { isC5UiEnabled } from './c5/c5Flag';
import {
  C5Panel,
  c5InkSlider,
  c5KeyRow,
  c5SectionHeader,
  c5SlipButton,
  c5WaxToggle,
  type C5Slider,
  type C5Toggle,
} from './c5/C5Panel';
import type { AudioSettings } from '../audio/AudioManager';
import {
  PAUSE_PANEL_BG,
  PAUSE_PANEL_BORDER,
  PAUSE_TITLE,
  PAUSE_BUTTON_BG,
  PAUSE_BUTTON_HOVER,
  PAUSE_BUTTON_TEXT,
  SETTINGS_SLIDER_BG,
  SETTINGS_SLIDER_FILL,
  SETTINGS_LABEL,
  SETTINGS_VALUE,
  SETTINGS_SECTION_HEADER,
  SETTINGS_KEY_BG,
  SETTINGS_KEY_TEXT,
  PAL_GREEN,
  PAL_RED,
  hexToCSS,
} from './palette';
import { getString } from './locale';
import { loadSettings, saveSettings } from './SettingsPersistence';

const OVERLAY_DEPTH = 2600;
const PANEL_WIDTH = 340;
const PANEL_HEIGHT = 446;
const PADDING = 16;
const SLIDER_WIDTH = 160;
const SLIDER_HEIGHT = 10;
const FADE_MS = 200;

interface SliderDef {
  label: string;
  getValue: () => number;
  setValue: (v: number) => void;
}

export class SettingsMenu {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private visible = false;

  // Callbacks
  private onClose: (() => void) | null = null;
  private getAudioSettings: (() => AudioSettings) | null = null;
  private setMasterVolume: ((v: number) => void) | null = null;
  private setSfxVolume: ((v: number) => void) | null = null;
  private setMusicVolume: ((v: number) => void) | null = null;
  private onSettingsChanged: (() => void) | null = null;

  // Slider visual state for updates
  private sliders: {
    fill: Phaser.GameObjects.Rectangle;
    valueText: Phaser.GameObjects.Text;
    track: Phaser.GameObjects.Rectangle;
    hitZone: Phaser.GameObjects.Zone;
    def: SliderDef;
  }[] = [];

  // Fullscreen toggle
  private fullscreenIndicator: Phaser.GameObjects.Text | null = null;

  // Touch controls toggle
  private touchControlsIndicator: Phaser.GameObjects.Text | null = null;
  private onTouchControlsChanged: ((visible: boolean) => void) | null = null;

  // Debug overlay toggle
  private debugOverlayIndicator: Phaser.GameObjects.Text | null = null;
  private onDebugOverlayChanged: ((visible: boolean) => void) | null = null;

  // C5 rendering (flag ON). Null when the Phaser panel is in use.
  private panel: C5Panel | null = null;
  private c5Sliders: C5Slider[] = [];
  private c5Toggles: C5Toggle[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(OVERLAY_DEPTH);
    this.container.setVisible(false);
    this.container.setAlpha(0);
  }

  /** Wire up audio controls. Call once after construction. */
  wireAudio(
    getSettings: () => AudioSettings,
    setMaster: (v: number) => void,
    setSfx: (v: number) => void,
    setMusic: (v: number) => void,
    onChanged?: () => void,
  ): void {
    this.getAudioSettings = getSettings;
    this.setMasterVolume = setMaster;
    this.setSfxVolume = setSfx;
    this.setMusicVolume = setMusic;
    this.onSettingsChanged = onChanged ?? null;
    if (isC5UiEnabled()) {
      this.buildC5Panel();
    } else {
      this.createPanel();
    }
  }

  setOnClose(cb: () => void): void {
    this.onClose = cb;
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    if (this.panel) {
      this.refreshC5();
      this.panel.show();
      return;
    }
    this.container.setVisible(true);
    this.refreshSliders();
    this.refreshFullscreen();
    this.refreshTouchControls();
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: FADE_MS,
    });
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    if (this.panel) {
      this.panel.hide();
      return;
    }
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: FADE_MS,
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  isVisible(): boolean {
    return this.visible;
  }

  /**
   * C5 rendering: a dark console sheet. Sliders are ink lines with a brass
   * bead, toggles are wax seals, section headers are specimen tags. Every
   * handler below is the same one the Phaser panel uses, so persistence and
   * the outbound callbacks are unchanged.
   */
  private buildC5Panel(): void {
    const panel = new C5Panel({
      title: getString('menu.settings.title'),
      variant: 'console',
      width: 360,
    });

    // ── Audio ──
    panel.body.appendChild(c5SectionHeader(getString('menu.settings.audio')));
    const sliderDefs: SliderDef[] = [
      {
        label: getString('menu.settings.audio.master_volume'),
        getValue: () => this.getAudioSettings!().masterVolume,
        setValue: (v) => this.setMasterVolume!(v),
      },
      {
        label: getString('menu.settings.audio.sfx_volume'),
        getValue: () => this.getAudioSettings!().sfxVolume,
        setValue: (v) => this.setSfxVolume!(v),
      },
      {
        label: getString('menu.settings.audio.music_volume'),
        getValue: () => this.getAudioSettings!().musicVolume,
        setValue: (v) => this.setMusicVolume!(v),
      },
    ];
    for (const def of sliderDefs) {
      const slider = c5InkSlider({
        label: def.label,
        getValue: def.getValue,
        setValue: def.setValue,
        onInput: () => this.onSettingsChanged?.(),
      });
      this.c5Sliders.push(slider);
      panel.body.appendChild(slider.row);
    }

    // ── Display ──
    panel.body.appendChild(c5SectionHeader(getString('menu.settings.display')));

    const fullscreen = c5WaxToggle({
      label: getString('menu.settings.display.fullscreen'),
      isOn: () => this.scene.scale.isFullscreen,
      onToggle: () => {
        if (this.scene.scale.isFullscreen) {
          this.scene.scale.stopFullscreen();
        } else {
          this.scene.scale.startFullscreen();
        }
        // The scale manager settles a frame or two after the request.
        this.scene.time.delayedCall(100, () => fullscreen.refresh());
        this.onSettingsChanged?.();
      },
    });
    this.c5Toggles.push(fullscreen);
    panel.body.appendChild(fullscreen.row);

    const touch = c5WaxToggle({
      label: getString('menu.settings.display.touch_controls'),
      isOn: () => loadSettings().showTouchControls !== false,
      onToggle: () => {
        const current = loadSettings();
        const newVal = current.showTouchControls === false;
        saveSettings({ ...current, showTouchControls: newVal });
        this.onTouchControlsChanged?.(newVal);
        this.onSettingsChanged?.();
      },
    });
    this.c5Toggles.push(touch);
    panel.body.appendChild(touch.row);

    const debug = c5WaxToggle({
      label: getString('menu.settings.display.debug_overlay'),
      isOn: () => loadSettings().showDebugOverlay === true,
      onToggle: () => {
        const current = loadSettings();
        const newVal = current.showDebugOverlay === false;
        saveSettings({ ...current, showDebugOverlay: newVal });
        this.onDebugOverlayChanged?.(newVal);
        this.onSettingsChanged?.();
      },
    });
    this.c5Toggles.push(debug);
    panel.body.appendChild(debug.row);

    // ── Controls ──
    panel.body.appendChild(c5SectionHeader(getString('menu.settings.controls')));
    const bindings: [string, string][] = [
      ['menu.settings.controls.move', 'menu.settings.controls.move_keys'],
      ['menu.settings.controls.light_attack', 'menu.settings.controls.light_attack_key'],
      ['menu.settings.controls.heavy_attack', 'menu.settings.controls.heavy_attack_key'],
      ['menu.settings.controls.dodge', 'menu.settings.controls.dodge_key'],
      ['menu.settings.controls.interact', 'menu.settings.controls.interact_key'],
      ['menu.settings.controls.inventory', 'menu.settings.controls.inventory_key'],
      ['menu.settings.controls.pause', 'menu.settings.controls.pause_key'],
    ];
    for (const [actionKey, bindKey] of bindings) {
      panel.body.appendChild(c5KeyRow(getString(actionKey), getString(bindKey)));
    }

    // ── Back ──
    const back = c5SlipButton(getString('menu.settings.back'), () => {
      this.hide();
      this.onClose?.();
    });
    back.classList.add('c5-p-narrow');
    // Footer, not body: Back stays reachable when the list has to scroll.
    panel.footer.appendChild(back);

    this.panel = panel;
  }

  /** Re-read every live source before the C5 sheet becomes visible. */
  private refreshC5(): void {
    for (const slider of this.c5Sliders) slider.refresh();
    for (const toggle of this.c5Toggles) toggle.refresh();
  }

  private createPanel(): void {
    const { width, height } = this.scene.scale;
    const cx = width / 2;
    const cy = height / 2;
    const left = cx - PANEL_WIDTH / 2;
    const top = cy - PANEL_HEIGHT / 2;

    // Panel background
    const bg = this.scene.add.rectangle(cx, cy, PANEL_WIDTH, PANEL_HEIGHT, PAUSE_PANEL_BG, 0.96);
    bg.setStrokeStyle(2, PAUSE_PANEL_BORDER);
    this.container.add(bg);

    // Title
    const title = this.scene.add.text(cx, top + PADDING, getString('menu.settings.title'), {
      fontSize: '18px',
      fontStyle: 'bold',
      color: hexToCSS(PAUSE_TITLE),
      align: 'center',
    });
    title.setOrigin(0.5, 0);
    this.container.add(title);

    let y = top + PADDING + 32;

    // ── Audio section ──
    y = this.addSectionHeader(left + PADDING, y, getString('menu.settings.audio'));
    y += 4;

    const _audioSettings = this.getAudioSettings!();
    const sliderDefs: SliderDef[] = [
      {
        label: getString('menu.settings.audio.master_volume'),
        getValue: () => this.getAudioSettings!().masterVolume,
        setValue: (v) => this.setMasterVolume!(v),
      },
      {
        label: getString('menu.settings.audio.sfx_volume'),
        getValue: () => this.getAudioSettings!().sfxVolume,
        setValue: (v) => this.setSfxVolume!(v),
      },
      {
        label: getString('menu.settings.audio.music_volume'),
        getValue: () => this.getAudioSettings!().musicVolume,
        setValue: (v) => this.setMusicVolume!(v),
      },
    ];

    for (const def of sliderDefs) {
      y = this.addSlider(left + PADDING, y, def);
      y += 6;
    }

    // ── Display section ──
    y += 6;
    y = this.addSectionHeader(left + PADDING, y, getString('menu.settings.display'));
    y += 4;
    y = this.addFullscreenToggle(left + PADDING, y);
    y += 4;
    y = this.addTouchControlsToggle(left + PADDING, y);
    y += 4;
    y = this.addDebugOverlayToggle(left + PADDING, y);

    // ── Controls section ──
    y += 10;
    y = this.addSectionHeader(left + PADDING, y, getString('menu.settings.controls'));
    y += 4;

    const bindings: [string, string][] = [
      ['menu.settings.controls.move', 'menu.settings.controls.move_keys'],
      ['menu.settings.controls.light_attack', 'menu.settings.controls.light_attack_key'],
      ['menu.settings.controls.heavy_attack', 'menu.settings.controls.heavy_attack_key'],
      ['menu.settings.controls.dodge', 'menu.settings.controls.dodge_key'],
      ['menu.settings.controls.interact', 'menu.settings.controls.interact_key'],
      ['menu.settings.controls.inventory', 'menu.settings.controls.inventory_key'],
      ['menu.settings.controls.pause', 'menu.settings.controls.pause_key'],
    ];

    for (const [actionKey, bindKey] of bindings) {
      y = this.addKeyBinding(left + PADDING, y, getString(actionKey), getString(bindKey));
    }

    // ── Back button ──
    y += 12;
    this.addButton(cx, y, getString('menu.settings.back'), () => {
      this.hide();
      if (this.onClose) this.onClose();
    });
  }

  private addSectionHeader(x: number, y: number, text: string): number {
    const header = this.scene.add.text(x, y, text, {
      fontSize: '11px',
      fontStyle: 'bold',
      color: hexToCSS(SETTINGS_SECTION_HEADER),
    });
    this.container.add(header);
    return y + 18;
  }

  private addSlider(x: number, y: number, def: SliderDef): number {
    // Label
    const label = this.scene.add.text(x, y, def.label, {
      fontSize: '10px',
      color: hexToCSS(SETTINGS_LABEL),
    });
    this.container.add(label);

    // Track
    const trackX = x + PANEL_WIDTH - PADDING * 2 - SLIDER_WIDTH - 40;
    const track = this.scene.add.rectangle(
      trackX + SLIDER_WIDTH / 2, y + 5,
      SLIDER_WIDTH, SLIDER_HEIGHT,
      SETTINGS_SLIDER_BG,
    );
    track.setOrigin(0.5, 0.5);
    this.container.add(track);

    // Fill
    const currentVal = def.getValue();
    const fillWidth = Math.max(2, currentVal * SLIDER_WIDTH);
    const fill = this.scene.add.rectangle(
      trackX, y + 5,
      fillWidth, SLIDER_HEIGHT,
      SETTINGS_SLIDER_FILL,
    );
    fill.setOrigin(0, 0.5);
    this.container.add(fill);

    // Value text
    const valueText = this.scene.add.text(
      trackX + SLIDER_WIDTH + 8, y,
      `${Math.round(currentVal * 100)}%`,
      { fontSize: '10px', color: hexToCSS(SETTINGS_VALUE) },
    );
    this.container.add(valueText);

    // Hit zone for interaction
    const hitZone = this.scene.add.zone(
      trackX + SLIDER_WIDTH / 2, y + 5,
      SLIDER_WIDTH + 10, SLIDER_HEIGHT + 10,
    );
    hitZone.setScrollFactor(0);
    hitZone.setDepth(OVERLAY_DEPTH + 1);
    hitZone.setInteractive({ useHandCursor: true });
    this.container.add(hitZone);

    const updateFromPointer = (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - trackX;
      const ratio = Math.max(0, Math.min(1, localX / SLIDER_WIDTH));
      def.setValue(ratio);
      fill.width = Math.max(2, ratio * SLIDER_WIDTH);
      valueText.setText(`${Math.round(ratio * 100)}%`);
      if (this.onSettingsChanged) this.onSettingsChanged();
    };

    hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      updateFromPointer(pointer);
      // Enable drag
      const moveHandler = (p: Phaser.Input.Pointer) => updateFromPointer(p);
      this.scene.input.on('pointermove', moveHandler);
      this.scene.input.once('pointerup', () => {
        this.scene.input.off('pointermove', moveHandler);
      });
    });

    this.sliders.push({ fill, valueText, track, hitZone, def });
    return y + 22;
  }

  private addFullscreenToggle(x: number, y: number): number {
    const label = this.scene.add.text(x, y, getString('menu.settings.display.fullscreen'), {
      fontSize: '10px',
      color: hexToCSS(SETTINGS_LABEL),
    });
    this.container.add(label);

    const isFs = this.scene.scale.isFullscreen;
    this.fullscreenIndicator = this.scene.add.text(
      x + PANEL_WIDTH - PADDING * 2 - 30, y,
      isFs ? 'ON' : 'OFF',
      {
        fontSize: '10px',
        fontStyle: 'bold',
        color: hexToCSS(isFs ? PAL_GREEN : PAL_RED),
      },
    );
    this.container.add(this.fullscreenIndicator);

    // Toggle zone
    const toggleZone = this.scene.add.zone(
      x + (PANEL_WIDTH - PADDING * 2) / 2, y + 6,
      PANEL_WIDTH - PADDING * 2, 18,
    );
    toggleZone.setScrollFactor(0);
    toggleZone.setDepth(OVERLAY_DEPTH + 1);
    toggleZone.setInteractive({ useHandCursor: true });
    this.container.add(toggleZone);

    toggleZone.on('pointerdown', () => {
      if (this.scene.scale.isFullscreen) {
        this.scene.scale.stopFullscreen();
      } else {
        this.scene.scale.startFullscreen();
      }
      // Update indicator after a short delay for the state to update
      this.scene.time.delayedCall(100, () => this.refreshFullscreen());
      if (this.onSettingsChanged) this.onSettingsChanged();
    });

    return y + 22;
  }

  private addTouchControlsToggle(x: number, y: number): number {
    const label = this.scene.add.text(x, y, getString('menu.settings.display.touch_controls'), {
      fontSize: '10px',
      color: hexToCSS(SETTINGS_LABEL),
    });
    this.container.add(label);

    const settings = loadSettings();
    const isOn = settings.showTouchControls !== false;
    this.touchControlsIndicator = this.scene.add.text(
      x + PANEL_WIDTH - PADDING * 2 - 30, y,
      isOn ? 'ON' : 'OFF',
      {
        fontSize: '10px',
        fontStyle: 'bold',
        color: hexToCSS(isOn ? PAL_GREEN : PAL_RED),
      },
    );
    this.container.add(this.touchControlsIndicator);

    const toggleZone = this.scene.add.zone(
      x + (PANEL_WIDTH - PADDING * 2) / 2, y + 6,
      PANEL_WIDTH - PADDING * 2, 18,
    );
    toggleZone.setScrollFactor(0);
    toggleZone.setDepth(OVERLAY_DEPTH + 1);
    toggleZone.setInteractive({ useHandCursor: true });
    this.container.add(toggleZone);

    toggleZone.on('pointerdown', () => {
      const current = loadSettings();
      const newVal = current.showTouchControls === false;
      saveSettings({ ...current, showTouchControls: newVal });
      this.refreshTouchControls();
      if (this.onTouchControlsChanged) this.onTouchControlsChanged(newVal);
      if (this.onSettingsChanged) this.onSettingsChanged();
    });

    return y + 22;
  }

  /** Register a callback that fires when the touch controls toggle changes. */
  setOnTouchControlsChanged(cb: (visible: boolean) => void): void {
    this.onTouchControlsChanged = cb;
  }

  private refreshTouchControls(): void {
    if (!this.touchControlsIndicator) return;
    const settings = loadSettings();
    const isOn = settings.showTouchControls !== false;
    this.touchControlsIndicator.setText(isOn ? 'ON' : 'OFF');
    this.touchControlsIndicator.setColor(hexToCSS(isOn ? PAL_GREEN : PAL_RED));
  }

  private addDebugOverlayToggle(x: number, y: number): number {
    const label = this.scene.add.text(x, y, getString('menu.settings.display.debug_overlay'), {
      fontSize: '10px',
      color: hexToCSS(SETTINGS_LABEL),
    });
    this.container.add(label);

    const settings = loadSettings();
    const isOn = settings.showDebugOverlay !== false;
    this.debugOverlayIndicator = this.scene.add.text(
      x + PANEL_WIDTH - PADDING * 2 - 30, y,
      isOn ? 'ON' : 'OFF',
      {
        fontSize: '10px',
        fontStyle: 'bold',
        color: hexToCSS(isOn ? PAL_GREEN : PAL_RED),
      },
    );
    this.container.add(this.debugOverlayIndicator);

    const toggleZone = this.scene.add.zone(
      x + (PANEL_WIDTH - PADDING * 2) / 2, y + 6,
      PANEL_WIDTH - PADDING * 2, 18,
    );
    toggleZone.setScrollFactor(0);
    toggleZone.setDepth(OVERLAY_DEPTH + 1);
    toggleZone.setInteractive({ useHandCursor: true });
    this.container.add(toggleZone);

    toggleZone.on('pointerdown', () => {
      const current = loadSettings();
      const newVal = current.showDebugOverlay === false;
      saveSettings({ ...current, showDebugOverlay: newVal });
      this.refreshDebugOverlay();
      if (this.onDebugOverlayChanged) this.onDebugOverlayChanged(newVal);
      if (this.onSettingsChanged) this.onSettingsChanged();
    });

    return y + 22;
  }

  setOnDebugOverlayChanged(cb: (visible: boolean) => void): void {
    this.onDebugOverlayChanged = cb;
  }

  private refreshDebugOverlay(): void {
    if (!this.debugOverlayIndicator) return;
    const settings = loadSettings();
    const isOn = settings.showDebugOverlay !== false;
    this.debugOverlayIndicator.setText(isOn ? 'ON' : 'OFF');
    this.debugOverlayIndicator.setColor(hexToCSS(isOn ? PAL_GREEN : PAL_RED));
  }

  private addKeyBinding(x: number, y: number, action: string, key: string): number {
    const label = this.scene.add.text(x, y, action, {
      fontSize: '9px',
      color: hexToCSS(SETTINGS_LABEL),
    });
    this.container.add(label);

    // Key badge
    const badgeX = x + PANEL_WIDTH - PADDING * 2 - 10;
    const badge = this.scene.add.rectangle(badgeX, y + 5, key.length * 7 + 10, 16, SETTINGS_KEY_BG);
    badge.setOrigin(1, 0.5);
    this.container.add(badge);

    const keyText = this.scene.add.text(badgeX - (key.length * 7 + 10) / 2, y, key, {
      fontSize: '8px',
      fontStyle: 'bold',
      color: hexToCSS(SETTINGS_KEY_TEXT),
      align: 'center',
    });
    keyText.setOrigin(0.5, 0);
    this.container.add(keyText);

    return y + 18;
  }

  private addButton(cx: number, y: number, text: string, onClick: () => void): void {
    const btnW = 120;
    const btnH = 28;

    const bg = this.scene.add.rectangle(cx, y + btnH / 2, btnW, btnH, PAUSE_BUTTON_BG);
    bg.setStrokeStyle(1, PAUSE_PANEL_BORDER);
    this.container.add(bg);

    const label = this.scene.add.text(cx, y + btnH / 2, text, {
      fontSize: '11px',
      fontStyle: 'bold',
      color: hexToCSS(PAUSE_BUTTON_TEXT),
      align: 'center',
    });
    label.setOrigin(0.5, 0.5);
    this.container.add(label);

    const hitZone = this.scene.add.zone(cx, y + btnH / 2, btnW, btnH);
    hitZone.setScrollFactor(0);
    hitZone.setDepth(OVERLAY_DEPTH + 1);
    hitZone.setInteractive({ useHandCursor: true });
    this.container.add(hitZone);

    hitZone.on('pointerover', () => bg.setFillStyle(PAUSE_BUTTON_HOVER));
    hitZone.on('pointerout', () => bg.setFillStyle(PAUSE_BUTTON_BG));
    hitZone.on('pointerdown', onClick);
  }

  private refreshSliders(): void {
    for (const slider of this.sliders) {
      const val = slider.def.getValue();
      slider.fill.width = Math.max(2, val * SLIDER_WIDTH);
      slider.valueText.setText(`${Math.round(val * 100)}%`);
    }
  }

  private refreshFullscreen(): void {
    if (!this.fullscreenIndicator) return;
    const isFs = this.scene.scale.isFullscreen;
    this.fullscreenIndicator.setText(isFs ? 'ON' : 'OFF');
    this.fullscreenIndicator.setColor(hexToCSS(isFs ? PAL_GREEN : PAL_RED));
  }

  destroy(): void {
    this.panel?.destroy();
    this.panel = null;
    this.c5Sliders = [];
    this.c5Toggles = [];
    this.container.destroy();
  }
}
